import { StanButton, StanToggle, StanTooltip } from '@/components';
import { cn } from '@/lib';
import { createIconElement } from '@/lib/utils/createIconElement';
import IconStripe from '@/assets/icons/payments/stripe.svg';
import IconCheckCircle from '@/assets/icons/ic-check-circle.svg';
import IconWarningCircle from '@/assets/icons/ic-alert-circle.svg';
import IconHelp from '@/assets/icons/ic-help-circle.svg';

interface StripeStatus {
    isOkay: boolean;
    isRestricted: boolean;
    isRejected: boolean;
    isAppealSubmitted: boolean;
    allowedToRestart: boolean;
    showStatus: boolean;
    statusDetails: string;
}

interface StripePaymentCardProps {
    isConnected: boolean;
    stripe: StripeStatus;
    isInstantPayoutEligible: boolean;
    instantPayoutEnabled: boolean;
    instantPayoutLoading: boolean;
    showAddPayPal: boolean;
    onManage: () => void;
    onConnect: () => void;
    onAppeal: () => void;
    onInstantPayoutToggle: (enabled: boolean) => void;
    onAddPayPal: () => void;
}

export const StripePaymentCard = ({
    isConnected,
    stripe,
    isInstantPayoutEligible,
    instantPayoutEnabled,
    instantPayoutLoading,
    showAddPayPal,
    onManage,
    onConnect,
    onAppeal,
    onInstantPayoutToggle,
    onAddPayPal,
}: StripePaymentCardProps) => {
    const showManageButton = isConnected && !stripe.allowedToRestart && !stripe.isRejected;
    const showRegisterButton = !stripe.isRejected && !showManageButton;

    return (
        <div className='flex w-full flex-col gap-2'>
            <div className={cn('bg-bg-subtle rounded-lg p-4', stripe.isRejected && 'pointer-events-none opacity-50')}>
                <div className='flex items-center justify-between'>
                    <div className='flex h-10 w-24 items-center'>
                        <IconStripe className='size-16' />
                    </div>
                    <div>
                        {showManageButton && (
                            <StanButton
                                name='stripe-connect-manage-button'
                                variant='outline'
                                size='sm'
                                onClick={onManage}
                            >
                                Manage
                            </StanButton>
                        )}
                        {showRegisterButton && (
                            <StanButton name='stripe-connect-connect-button' size='sm' onClick={onConnect}>
                                Register
                            </StanButton>
                        )}
                    </div>
                </div>
            </div>

            {stripe.showStatus && <StripeStatusMessage stripe={stripe} onManage={onManage} onAppeal={onAppeal} />}

            {isInstantPayoutEligible && (
                <InstantPayoutToggle
                    enabled={instantPayoutEnabled}
                    loading={instantPayoutLoading}
                    onToggle={onInstantPayoutToggle}
                />
            )}

            {showAddPayPal && (
                <button
                    className='text-body-sm text-brand-main ml-auto w-fit cursor-pointer text-right font-medium'
                    onClick={onAddPayPal}
                >
                    + Add PayPal
                </button>
            )}
        </div>
    );
};

interface StripeStatusMessageProps {
    stripe: StripeStatus;
    onManage: () => void;
    onAppeal: () => void;
}

const StripeStatusMessage = ({ stripe, onManage, onAppeal }: StripeStatusMessageProps) => {
    const statusMessage =
        stripe.isOkay && !stripe.isRestricted && !stripe.isRejected
            ? 'You can accept payments and receive payouts.'
            : stripe.statusDetails;

    return (
        <div className='flex items-center gap-1.5 py-2'>
            <div className='shrink-0'>
                {createIconElement(stripe.isOkay ? IconCheckCircle : IconWarningCircle, {
                    className: cn('size-6', stripe.isOkay ? 'text-brand-main' : 'text-negative-bold'),
                })}
            </div>
            <p className='text-body-sm text-gray flex-1'>
                {statusMessage}
                {stripe.isRestricted && (
                    <>
                        {' '}
                        <button className='text-brand-main font-bold hover:underline' onClick={onManage}>
                            Update now.
                        </button>
                    </>
                )}
                {stripe.isRejected && !stripe.isAppealSubmitted && (
                    <>
                        {' '}
                        <button className='text-brand-main font-bold hover:underline' onClick={onAppeal}>
                            Request Appeal.
                        </button>
                    </>
                )}
            </p>
        </div>
    );
};

interface InstantPayoutToggleProps {
    enabled: boolean;
    loading: boolean;
    onToggle: (enabled: boolean) => void;
}

const InstantPayoutToggle = ({ enabled, loading, onToggle }: InstantPayoutToggleProps) => (
    <div className='flex items-center gap-2'>
        <StanToggle checked={enabled} onChange={e => onToggle(e.target.checked)} disabled={loading} toggleSize='sm' />
        <span className='text-caption-1-regular text-gray'>Stripe Instant Payouts</span>
        <StanTooltip
            content='Stripe payouts may take up to 3 business days. You can use Instant Payouts for a 1% processing fee.'
            position='top'
        >
            {createIconElement(IconHelp, {
                className: 'size-5 text-brand-main cursor-help',
            })}
        </StanTooltip>
    </div>
);
